import React, { useState } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Menu, X, ChevronDown, Calculator, BookOpen, PenSquare, User, School, Wallet, Book } from 'lucide-react';

function Navigation() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [activeMenu, setActiveMenu] = useState(null);

  const getCurrentPageName = () => {
    const path = location.pathname;
    if (path === '/dashboard') return 'Dashboard';
    if (path.startsWith('/calculators')) return 'Calculators';
    if (path.startsWith('/training')) return 'Training';
    if (path.startsWith('/articles')) return 'Articles';
    if (path === '/account') return 'Account';
    return '';
  };

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  // Article categories matching Articles.js
  const articleCategories = [
    { id: 'all', name: 'All Articles', path: '/articles' },
    //{ id: 'investing', name: 'Investing', path: '/articles?category=investing' },
    //{ id: 'retirement', name: 'Retirement Planning', path: '/articles?category=retirement' },
    //{ id: 'taxes', name: 'Tax Strategy', path: '/articles?category=taxes' },
    //{ id: 'credit', name: 'Credit & Debt', path: '/articles?category=credit' },
    //{ id: 'estate', name: 'Estate Planning', path: '/articles?category=estate' }
  ];

  const renderArticlesDropdown = () => (
    <div className="relative group">
      <Link to="/articles" className="text-white hover:text-gray-300 px-3 py-2 inline-flex items-center">
        <BookOpen className="w-4 h-4 mr-1" />
        Articles <ChevronDown className="ml-1 h-4 w-4" />
      </Link>
      <div className="absolute left-0 hidden group-hover:block w-64 py-1 bg-white rounded-md shadow-lg z-50">
        {articleCategories.map(category => (
          <Link
            key={category.id}
            to={category.path}
            className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
          >
            {category.name}
          </Link>
        ))}
        <div className="border-t border-gray-100 mt-1 pt-1">
          <Link
            to="/articles/create"
            className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 flex items-center"
          >
            <PenSquare className="w-4 h-4 mr-2" />
            Create Article
          </Link>
        </div>
      </div>
    </div>
  );


  const renderMobileArticlesMenu = () => (
    <div className="relative">
      <button className="text-white hover:text-gray-300 w-full text-left flex items-center justify-between" 
        onClick={() => setActiveMenu('articles')}>
        <span className="flex items-center">
          <BookOpen className="w-4 h-4 mr-2" />
          Articles
        </span>
        <ChevronDown className="w-4 h-4" />
      </button>
      <div className={`mt-2 ml-6 space-y-2 ${activeMenu === 'articles' ? 'block' : 'hidden'}`}>
        {articleCategories.map(category => (
          <Link
            key={category.id}
            to={category.path}
            className="block px-3 py-2 text-white hover:bg-gray-700 rounded pl-6 text-sm"
          >
            {category.name}
          </Link>
        ))}
        <Link
          to="/articles/create"
          className="block px-3 py-2 text-white hover:bg-gray-700 rounded pl-6 text-sm flex items-center"
        >
          <PenSquare className="w-4 h-4 mr-2" />
          Create Article
        </Link>
      </div>
    </div>
  );

  return (
    <nav className="bg-gray-800">
      <div className="max-w-7xl mx-auto px-4">
        <div className="flex justify-between h-16">
          {/* Logo and Brand */}
          <div className="flex items-center">
            <Link to="/" className="text-white text-xl font-bold">
              cilxchange
            </Link>
          </div>

          {user ? (
            <>
              {/* Desktop Navigation */}
              <div className="hidden md:flex items-center space-x-4">
                <div className="text-gray-300">
                  {user.email} : {getCurrentPageName()}
                </div>

                <Link to="/dashboard" className="text-white hover:text-gray-300 px-3 py-2">
                  Dashboard
                </Link>
                
                
                {/* Portfolio Menu */}
                <div className="relative group">
                  <Link to="" className="text-white hover:text-gray-300 px-3 py-2 inline-flex items-center">
                    <Wallet className="w-4 h-4 mr-1" />
                    Consumers <ChevronDown className="ml-1 h-4 w-4" />
                  </Link>
                  <div className="absolute left-0 hidden group-hover:block w-64 py-1 bg-white rounded-md shadow-lg z-50">
                  <Link to="/consumers" className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100">
                    Consumers
                  </Link>
                  <Link to="/my-skills" className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100">
                    My Skills
                  </Link>
                  <Link to="/skills-exchange" className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100">
                    Skills Exchange
                  </Link>
                  <Link to="/my-peer-support-groups" className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100">
                    My Peer Support Groups
                  </Link>
                  

                  </div>
                </div>

                {/* Calculators Menu */}
                <div className="relative group">
                  <Link to="/calculators" className="text-white hover:text-gray-300 px-3 py-2 inline-flex items-center">
                    <Calculator className="w-4 h-4 mr-1" />
                    Calculators <ChevronDown className="ml-1 h-4 w-4" />
                  </Link>
                  <div className="absolute left-0 hidden group-hover:block w-64 py-1 bg-white rounded-md shadow-lg z-50">
                    <Link to="/calculators/budget" className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100">
                      Budget Calculator
                    </Link>
                    <Link to="/calculators/true-cost-credit" className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100">
                      True Cost of Credit
                    </Link>
                  </div>
                </div>

                 {/* Training Menu */}
                 {/*
                 <div className="relative group">
                  <Link to="" className="text-white hover:text-gray-300 px-3 py-2 inline-flex items-center">
                    <School className="w-4 h-4 mr-1" />
                    Training <ChevronDown className="ml-1 h-4 w-4" />
                  </Link>
                  <div className="absolute left-0 hidden group-hover:block w-64 py-1 bg-white rounded-md shadow-lg z-50">
                    <Link to="/training/budget" className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100">
                      Budgeting
                    </Link>
                                        
                  </div>
                </div>
                */}  

                {/* Articles Menu */}
                {renderArticlesDropdown()}

                {/* Account Menu */}
                <div className="relative group">
                  <Link to="/account" className="text-white hover:text-gray-300 px-3 py-2 inline-flex items-center">
                    <User className="w-4 h-4 mr-1" />
                    Account <ChevronDown className="ml-1 h-4 w-4" />
                  </Link>
                  <div className="absolute left-0 hidden group-hover:block w-64 py-1 bg-white rounded-md shadow-lg z-50">
                    <Link to="/account" className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100">
                      Account Settings
                    </Link>
                    <Link to="/subscriptionplans" className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100">
                      Subscription Plans
                    </Link>
                    {/*user?.roles?.some(role => role.permissions.includes('admin')) && (*/}

                      <Link to="/admin" className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100">
                        Admin Panel
                      </Link>
                   {/*} )*/}
                    <div className="border-t border-gray-100 mt-1 pt-1">
                      <button
                        onClick={handleLogout}
                        className="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                      >
                        Sign Out
                      </button>
                    </div>
                  </div>
                </div>
              </div>

              {/* Mobile menu button */}
              <div className="md:hidden flex items-center">
                <button
                  onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
                  className="text-gray-400 hover:text-white p-2"
                >
                  {isMobileMenuOpen ? (
                    <X className="h-6 w-6" />
                  ) : (
                    <Menu className="h-6 w-6" />
                  )}
                </button>
              </div>
            </>
          ) : (
            // Non-authenticated navigation
            <div className="hidden md:flex items-center space-x-4">
              <Link to="/login" className="text-white hover:text-gray-300">
                Sign in
              </Link>
              <Link to="/register" className="bg-blue-500 text-white px-4 py-2 rounded hover:bg-blue-600">
                Start Free Trial
              </Link>
            </div>
          )}
        </div>
      </div>

      ```jsx
{/* Mobile menu */}
{isMobileMenuOpen && user && (
  <div className="md:hidden bg-gray-800 p-4">
    <div className="space-y-4">
      <div className="text-gray-300 text-sm">
        {user.email} : {getCurrentPageName()}
      </div>
      
      <div className="flex flex-col space-y-2">
        <Link to="/dashboard" className="text-white hover:text-gray-300 flex items-center">
          <Calculator className="w-4 h-4 mr-2" />
          Dashboard
        </Link>

        {/* Portfolio Mobile Menu */}
        <div className="relative">
          <button className="text-white hover:text-gray-300 w-full text-left flex items-center justify-between" 
            onClick={() => setActiveMenu(activeMenu === 'portfolio' ? null : 'portfolio')}>
            <span className="flex items-center">
              <Wallet className="w-4 h-4 mr-2" />
              Consumers
            </span>
            <ChevronDown className="w-4 h-4" />
          </button>
          <div className={`mt-2 ml-6 space-y-2 ${activeMenu === 'portfolio' ? 'block' : 'hidden'}`}>
            <Link to="/consumers" className="block text-white hover:text-gray-300">
              Consumers
            </Link>
            <Link to="/my-skills" className="block text-white hover:text-gray-300">
              My Skills
            </Link>
            <Link to="/skills-exchange" className="block text-white hover:text-gray-300">
              Skills Exchange
            </Link>
            <Link to="/my-peer-support-groups" className="block text-white hover:text-gray-300">
              My Peer Support Groups
            </Link>
          </div>
        </div>

        {/* Calculators Mobile Menu */}
        <div className="relative">
          <button className="text-white hover:text-gray-300 w-full text-left flex items-center justify-between" 
            onClick={() => setActiveMenu(activeMenu === 'calculator' ? null : 'calculator')}>
            <span className="flex items-center">
              <Calculator className="w-4 h-4 mr-2" />
              Calculators
            </span>
            <ChevronDown className="w-4 h-4" />
          </button>
          <div className={`mt-2 ml-6 space-y-2 ${activeMenu === 'calculator' ? 'block' : 'hidden'}`}>
            <Link to="/calculators/budget" className="block text-white hover:text-gray-300">
              Budget Calculator
            </Link>
            <Link to="/calculators/true-cost-credit" className="block text-white hover:text-gray-300">
              True Cost of Credit
            </Link>
          </div>
        </div>

        {/* Training Mobile Menu */}
        {/*
        <div className="relative">
          <button className="text-white hover:text-gray-300 w-full text-left flex items-center justify-between" 
            onClick={() => setActiveMenu(activeMenu === 'training' ? null : 'training')}>
            <span className="flex items-center">
              <School className="w-4 h-4 mr-2" />
              Training
            </span>
            <ChevronDown className="w-4 h-4" />
          </button>
          <div className={`mt-2 ml-6 space-y-2 ${activeMenu === 'training' ? 'block' : 'hidden'}`}>
            <Link to="/training/budget" className="block text-white hover:text-gray-300">
              Budgeting
            </Link>
          </div>
        </div>
        */}

        {renderMobileArticlesMenu()}

        {/* Account Mobile Menu */}
        <div className="relative">
          <button className="text-white hover:text-gray-300 w-full text-left flex items-center justify-between" 
            onClick={() => setActiveMenu(activeMenu === 'account' ? null : 'account')}>
            <span className="flex items-center">
              <User className="w-4 h-4 mr-2" />
              Account
            </span>
            <ChevronDown className="w-4 h-4" />
          </button>
          <div className={`mt-2 ml-6 space-y-2 ${activeMenu === 'account' ? 'block' : 'hidden'}`}>
            <Link to="/account" className="block text-white hover:text-gray-300">
              Account Settings
            </Link>
            <Link to="/subscriptionplans" className="block text-white hover:text-gray-300">
              Subscription Plans
            </Link>
            {user?.roles?.some(role => role.permissions.includes('admin')) && (
              <Link to="/admin" className="block text-white hover:text-gray-300">
                Admin Panel
              </Link>
            )}
            <button
              onClick={handleLogout}
              className="block w-full text-left text-white hover:text-gray-300"
            >
              Sign Out
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
)}
    </nav>
  );
}

export default Navigation;
